import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ban, Check, CheckCheck, Download, FileText, Plus, Save } from 'lucide-react';
import { Button, buttonVariants } from './ui/button';
import { cn } from '../lib/utils';

/**
 * PageHeader - Componente de cabeçalho de página reutilizável
 *
 * @param {string} title - Título da página
 * @param {Array} breadcrumbs - Ancestrais da tela (a tela atual é o título e não entra)
 * @param {React.ReactNode} contextContent - Relatórios: filtros e período
 * @param {Array} actions - Cadastros: Voltar + Salvar; relatórios: saída (ex. Exportar CSV). Cada ação leva `icon` (chave de ACTION_ICONS)
 * @param {Array} additionalActions - Cadastros/relatórios: ações extras (ex. Cancelar, Novo registro)
 * @param {React.ReactNode} rightContent - Substitui todos os blocos (uso legado)
 */
const ACTION_ICONS = {
    new: Plus,
    back: ArrowLeft,
    save: Save,
    export: Download,
    cancel: Ban,
    finish: CheckCheck,
    approve: Check,
    pdf: FileText,
};

/** `icon` aceita uma chave de ACTION_ICONS ou um elemento React. */
function renderIcon(icon) {
    if (!icon) return null;
    const Icon = typeof icon === 'string' ? ACTION_ICONS[icon] : null;
    return <span className="shrink-0">{Icon ? <Icon className="w-4 h-4" /> : icon}</span>;
}

function renderAction(action, index) {
    if (action.as === Link && action.to) {
        return (
            <Link
                key={index}
                to={action.to}
                className={cn(
                    buttonVariants({ variant: action.variant || 'default' }),
                    action.disabled && 'pointer-events-none opacity-50',
                    action.className
                )}
            >
                {renderIcon(action.icon)}
                {action.label}
            </Link>
        );
    }

    return (
        <Button
            key={index}
            variant={action.variant || 'default'}
            onClick={action.onClick}
            disabled={action.disabled}
            type={action.type || 'button'}
            form={action.form}
            className={action.className}
        >
            {renderIcon(action.icon)}
            {action.label}
        </Button>
    );
}

function ActionsDivider() {
    return <div className="hidden sm:block h-8 w-px bg-gray-200 shrink-0" aria-hidden />;
}

function PageHeader({
    title,
    breadcrumbs = [],
    contextContent = null,
    actions = [],
    additionalActions = [],
    rightContent = null,
}) {

    const hasContext = contextContent != null;
    const hasAdditional = additionalActions.length > 0;
    const hasStandard = actions.length > 0;

    const actionBlocks = [];

    if (hasContext) {
        actionBlocks.push(
            <div key="context" className="flex items-center gap-3 flex-wrap">
                {contextContent}
            </div>
        );
    }
    if (hasAdditional) {
        actionBlocks.push(
            <div key="additional" className="flex items-center gap-3">
                {additionalActions.map(renderAction)}
            </div>
        );
    }
    if (hasStandard) {
        actionBlocks.push(
            <div key="standard" className="flex items-center gap-3">
                {actions.map(renderAction)}
            </div>
        );
    }

    return (
        <div className="sticky top-16 z-20 bg-white border-b border-gray-200 shadow-sm -mx-4 md:-mx-8 px-4 md:px-8 py-4 -mt-4 md:-mt-8">
            <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                    {breadcrumbs.length > 0 && (
                        <nav className="flex items-center space-x-2 text-sm text-gray-500">
                            {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    )}
                                    {crumb.path ? (
                                        <Link
                                            to={crumb.path}
                                            className="hover:text-gray-700 transition-colors"
                                        >
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span>
                                            {crumb.label}
                                        </span>
                                    )}
                                </React.Fragment>
                            ))}
                        </nav>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                </div>

                {rightContent != null ? (
                    <div className="flex items-center gap-3 flex-wrap justify-end min-w-0 shrink">
                        {rightContent}
                    </div>
                ) : actionBlocks.length > 0 ? (
                    <div className="flex items-center gap-3 flex-wrap justify-end min-w-0 shrink">
                        {actionBlocks.map((block, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <ActionsDivider />}
                                {block}
                            </React.Fragment>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default PageHeader;
